import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field, useFormikContext } from "formik";
import {
  Col,
  Container,
  Row,
  Button,
  FormGroup,
  Input,
  FormFeedback,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { ReactstrapInput } from "reactstrap-formik";

import * as Yup from "yup";

import FormHelpers from "../../helpers/FormHelpers";
import ScrollToFieldError from "./Validation/ScrollToFieldError";
import InfoTooltip from "../UI/Progress/InfoTooltip";
import OptGroupSelectDropDown from "./Fields/OptGroupSelectDropDown";

import { configuratorActions } from "../../store/configurator-slice";
import { cardPreviewActions } from "../../store/card-preview-slice";
import { fetchOccasionData } from "../../store/card-preview-actions";
import { sendCartData } from "../../store/configurator-actions";

function RequestForm() {
  const dispatch = useDispatch();

  const addingToCart = useSelector((state) => state.configurator.AddingToCart);
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );
  const requestDetails = currentRequest.DonorRequest;

  const currentProductState =
    currentRequest.Products[currentRequest.CurrentProduct];
  const currentPreviewState = useSelector((state) => state.cardPreview);

  const isUpdating = requestDetails.Id;

  // prepare occasions list
  const occasions = currentRequest.Lists.Occasions.MassRequestOccasions.filter(
    (item) => item.Parent !== null
  )
    .map((item) => {
      return {
        id: item.Id,
        name: item.Description,
        optionGroup: item.Parent.Description,
      };
    })
    .reduce((acc, option) => {
      const accOptionGroup = acc[option.optionGroup] || [];

      return {
        ...acc,
        [option.optionGroup]: [...accOptionGroup, option],
      };
    }, {});

    const sortedOccasions = Object.keys(occasions).sort().reduce(
      (obj, key) => { 
        obj[key] = occasions[key]; 
        return obj;
      }, 
      {}
    );

  let defaultIntentionConfig = {
      placeholder: "2 lines of 32 characters each",
      rows: 2,
      cols: 32,
      max: 64
    };

  // init state for form fields
  const [intentionConfig, setIntentionConfig] = useState(
    defaultIntentionConfig
  );

  const formRef = useRef(null);
  const intentionRef = useRef(null);

  const validationRules = {
    intention: Yup.string().required("Required"),
    cardType: Yup.string().required("Required"),
    occasion: Yup.string().required("Required").notOneOf(['0'], 'Required'),
  };

  const formSchema = Yup.object().shape(validationRules);

  function formSubmitHandler(values) {
    // if request is a no card, add to cart right now and move to last step
    dispatch(configuratorActions.setRequest(values));
    dispatch(cardPreviewActions.setFieldFocus(""));
    FormHelpers.scrollToTop();
  }

  const setIntention = (cardTypeValue) => {
    dispatch(configuratorActions.setCardType(cardTypeValue));
  };

  useEffect(() => {

    if(intentionRef.current !== null) {
      const maxLength = Number(intentionConfig.max);
      const inputLength = intentionRef.current.props.value.length;
      const intention = formRef.current.values.intention;
      if (inputLength > maxLength) {
        formRef.current.setFieldValue('intention', intention.substring(0, maxLength));
        dispatch(
          cardPreviewActions.setFieldValue({
            key: "CardIntention",
            value: intention.substring(0, maxLength)
          })
        );
      }
    }
    
  }, [intentionConfig]);

  const cardTypeClickHandler = (event) => {
    setIntention(event.target.value);
  };

  const inputFocusHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(e.target.name));
  };

  const inputBlurHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  const occasionSelectHandler = (occasionId) => {
    dispatch(cardPreviewActions.setOccasionLoading());
    dispatch(fetchOccasionData(occasionId));
  };

  return (
    <div className="cfg-input-form cfg-request" data-step="request">
      <Formik
        initialValues={{
          intention: requestDetails.Intention,
          cardType: requestDetails.CardTypeValue,
          occasion: requestDetails.Occasion,
        }}
        validationSchema={formSchema}
        onSubmit={formSubmitHandler}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="text-start">
            <ScrollToFieldError />
            <Container className="cfg-form-section">
            
                <Row className="mb-2">
                  <Col xs="12">
                    <InfoTooltip
                      id="occasionHelp"
                      title="Help"
                      text="Click the down arrow to select the option that best describes the reason for your request."
                    />
                    <h4 className="required">Select an Occasion:</h4>
                    <FormGroup>
                      <OptGroupSelectDropDown
                        name="occasion"
                        error={errors.occasion}
                        required={true}
                        emptyOption="Select..."
                        options={sortedOccasions}
                        defaultValue={values.occasion}
                        onChange={(e) => {
                          handleChange(e);
                          occasionSelectHandler(e.currentTarget.value);
                        }}
                        className={`form-control ${
                          errors.occasion && touched.occasion
                            ? "is-invalid"
                            : ""
                        }`}
                        onBlur={handleBlur}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              
              <Row>
                <Col xs="12">
                  <InfoTooltip
                    id="intentionHelp"
                    title="Help"
                    text="The person(s) on whose behalf the Mass is to be celebrated."
                  />
                  <h4 className="required">Name of person(s):</h4>
                  <FormGroup>
                    <Input
                      name="intention"
                      type="textarea"
                      id="intention"
                      ref={intentionRef}
                      datamax={intentionConfig.max}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      value={values.intention}
                      rows={intentionConfig.rows}
                      cols={intentionConfig.cols}
                      className="cfg-intention-input parse-new-lines"
                      placeholder={intentionConfig.placeholder}
                      onKeyUp={FormHelpers.handleMultilineKeyUp}
                      onKeyDown={FormHelpers.handleMultilineKeyDown}
                      onPaste={FormHelpers.handleMultilineUpdate}
                      onChange={(e) => {
                        FormHelpers.handleMultilineUpdate(e);
                        handleChange(e);
                        dispatch(
                          cardPreviewActions.setFieldValue({
                            key: "CardIntention",
                            value: e.target.value,
                          })
                        );
                      }}
                      onFocus={(e) => {
                        inputFocusHandler(e);
                      }}
                      onBlur={(e) => {
                        FormHelpers.handleMultilineUpdate(e);
                        FormHelpers.cleanupEmptyLines(e, values, 'intention');
                        handleBlur(e);
                        inputBlurHandler();
                      }}
                      invalid={Boolean(
                        touched["intention"] && errors["intention"]
                      )}
                    />
                    {touched["intention"] && errors["intention"] ? (
                      <FormFeedback>{errors["intention"]}</FormFeedback>
                    ) : (
                      ""
                    )}
                  </FormGroup>
                </Col>
                <Col xs="12">
                  <h4 className="required">Tell Us If You Need A Card:</h4>
                  <FormGroup>
                  <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="sendtome"
                          onClick={cardTypeClickHandler}
                        />
                        Please send the card to me
                      </label>
                    </div>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="sendtosomeone"
                          onClick={cardTypeClickHandler}
                        />
                        Mail the card to someone for me
                      </label>
                    </div>
                    <div
                      className={`form-check ${
                        errors.cardType && touched.cardType ? "is-invalid" : ""
                      }`}
                    >
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="ecard"
                          onClick={cardTypeClickHandler}
                        />
                        Send an eCard for me
                      </label>
                    </div>
                    {errors.cardType && touched.cardType && (
                      <div className="invalid-feedback">{errors.cardType}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              
              {/* <DebugOutput
                errors={errors}
                values={FormHelpers.getValues(values)}
              /> */}
            </Container>
            <Button
              type="submit"
              size="sm"
              color="primary"
              className="float-end"
            >
              Next
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default RequestForm;
